import { markHostedBootWait } from '#~/routes/root/boot-budget.js';
/** Registration page — immediately redirects to Auth0 signup. */

// ENG-4175: the segmentation wizard was RELOCATED from this pre-auth page to a
// post-auth first-login step (routes/agent/setup/post-auth-prequal.tsx). This page
// now ALWAYS redirects straight to Auth0 signup — the feature-flag gate, the
// wizard branch, and the prequal phase/latch state are all removed.
// RedirectToAuth still records registration intent for funnel attribution. Account
// freshness comes from Auth0's first_login claim after authentication.

import { fail, OAuthTimeout } from '#~/auth/index.js';
import type { LocationState } from '#~/auth/index.js';
import {
  buildHostedAuth0ExtParams,
  makeAuthJourneyAppState,
  resolveAuthJourneyForRoute,
  setActiveAuthJourney
} from '#~/auth/index.js';
import { Paths } from '../constants.js';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader } from '@studio/design-system/loader';
import { toString, winnowObject } from '@tryiris-ai/js-types';
import pTimeout from 'p-timeout';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { trackAuthLoginFailed, useAnalytics } from '#~/analytics/index.js';
import { createAuthJourneyTracker } from '#~/analytics/domains/index.js';

/** Fires REGISTRATION_STARTED and initiates the Auth0 OAuth signup redirect. */
const RedirectToAuth = ({
  callbackUri,
  locationState,
  redirectUri
}: {
  callbackUri: string;
  locationState?: LocationState;
  redirectUri: string;
}): ReactElement => {
  const { loginWithRedirect } = useAuth0();
  const { track } = useAnalytics();

  useEffect(() => {
    const journey = resolveAuthJourneyForRoute('studio_register', locationState);

    setActiveAuthJourney(journey);

    createAuthJourneyTracker(track).trackRegistrationStarted(journey);

    const cancelHandoff = markHostedBootWait();
    pTimeout(
      loginWithRedirect(
        winnowObject({
          appState: makeAuthJourneyAppState(redirectUri, journey),
          authorizationParams: winnowObject({
            ...buildHostedAuth0ExtParams(journey),
            redirect_uri: callbackUri,
            screen_hint: 'signup'
          })
        })
      ),
      {
        milliseconds: OAuthTimeout
      }
    ).catch((error: Error) => {
      cancelHandoff();
      // ENG-4348 stage 3: dual-emits auth_login_failed + legacy oauth_error
      // through the shared helper (journey props injected there) so this site
      // can't drift from the canonical pair during the parity window.
      trackAuthLoginFailed(track, journey, { error_message: error.message });
      throw fail('6E4C', error);
    });
  }, [callbackUri, locationState, loginWithRedirect, redirectUri, track]);

  return <Loader />;
};

export const Register = (): ReactElement => {
  const { isAuthenticated } = useAuth0();
  const location = useLocation();
  const locationState = location.state as LocationState;

  const redirectUri = toString(locationState?.redirect, {
    default: '/'
  });
  const callbackUri = `${window.location.protocol}//${window.location.host}/${Paths.oauth2.callback}`;

  if (isAuthenticated) {
    return <Navigate replace to={redirectUri} />;
  }

  return <RedirectToAuth callbackUri={callbackUri} redirectUri={redirectUri} />;
};
